import { useRef, useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '@/components/SEO';
import ClassCalendar from '@/components/ClassCalendar';
import TrackedBookingLink from '@/components/TrackedBookingLink';
import WeeklyScheduleTable from '@/components/WeeklyScheduleTable';
import { CONTACT_INFO } from '@/lib/constants';
import { Calendar, Clock, Download, ShieldCheck } from 'lucide-react';

const FadeInWhenVisible = ({ children, delay = 0 }: { children: React.ReactNode, delay?: number }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-100px" }}
    transition={{ duration: 0.8, delay, ease: "easeOut" }}
  >
    {children}
  </motion.div>
);

export default function Schedule() {
  const scheduleTableRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownloadScheduleImage = async () => {
    const node = scheduleTableRef.current;
    const scrollableEl = node?.querySelector<HTMLElement>('.overflow-x-auto');
    if (!node || !scrollableEl || isDownloading) return;
    setIsDownloading(true);

    // html-to-image bakes in each element's *current* computed size when it
    // clones the DOM, so passing width/style options to toPng can't undo the
    // horizontal-scroll clipping on narrow screens. Widening just the
    // scrollable div isn't enough either — its ancestors (including `node`,
    // the element actually passed to toPng) don't auto-grow to contain that
    // overflow, so the capture area stays clipped to their old width. Every
    // element in the chain from `node` down to the scrollable div has to be
    // widened to fit its content before capture, then restored after.
    const chain: HTMLElement[] = [];
    for (let el: HTMLElement | null = scrollableEl; el; el = el.parentElement) {
      chain.push(el);
      if (el === node) break;
    }
    const previousInlineStyles = chain.map((el) => el.getAttribute('style'));

    try {
      scrollableEl.style.overflow = 'visible';
      for (const el of chain) {
        el.style.width = 'max-content';
      }
      // Let the browser reflow with the new widths before we snapshot it.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      const { toPng } = await import('html-to-image');
      const dataUrl = await toPng(node, {
        pixelRatio: 2,
        backgroundColor: '#ffffff',
      });
      const link = document.createElement('a');
      link.download = 'delta-bjj-weekly-schedule.png';
      link.href = dataUrl;
      link.click();
    } catch (error) {
      console.error('Failed to download schedule image', error);
    } finally {
      chain.forEach((el, i) => {
        const previous = previousInlineStyles[i];
        if (previous === null || previous === undefined) {
          el.removeAttribute('style');
        } else {
          el.setAttribute('style', previous);
        }
      });
      setIsDownloading(false);
    }
  };

  return (
    <div className="bg-brand-black min-h-screen">
      <SEO
        title="BJJ Classes & Training Culver City | Delta BJJ"
        description="Join Delta BJJ in Culver City for elite Gi & No-Gi Brazilian Jiu-Jitsu training. Flexible morning, midday & evening classes for all levels. Book a free trial!"
        url="https://gersonsanginittobjj.com/schedule"
      />

      {/* Hero Header */}
      <section className="relative pt-48 pb-32 px-6 lg:px-12 border-b border-white/10">
        <div className="max-w-7xl mx-auto text-center">
          <FadeInWhenVisible>
            <span className="text-brand-silver font-display text-xs tracking-[0.4em] uppercase mb-8 block font-bold">Your Training</span>
            <h1 className="text-6xl md:text-9xl font-display font-bold text-white tracking-tighter leading-none mb-12">
              DELTA BJJ <br />
              <span className="blue-gradient-text">Class Calendar.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/50 max-w-2xl mx-auto font-light tracking-wide uppercase">
            Flexible Brazilian Jiu-Jitsu Training for Your Active Lifestyle
            </p>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* Calendar Section - Premium Container */}
      <section className="py-24 md:py-32 px-6 lg:px-12 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20 grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              { icon: Calendar, title: "Flexible", desc: "Classes 7 days a week" },
              { icon: Clock, title: "Diverse", desc: "Morning & evening slots" },
              { icon: ShieldCheck, title: "Excellence", desc: "World-class instructors" },
            ].map((item, i) => {
              const Icon = item.icon;
              return (
                <FadeInWhenVisible key={i} delay={i * 0.1}>
                  <div className="flex gap-6 items-center">
                    <div className="w-16 h-16 rounded-full bg-brand-blue flex items-center justify-center text-white shrink-0 shadow-lg shadow-brand-blue/20">
                      <Icon size={32} strokeWidth={2.5} color="white" className="block" />
                    </div>
                    <div>
                      <h3 className="text-lg font-display font-bold text-brand-black tracking-tight uppercase">{item.title}</h3>
                      <p className="text-gray-500 text-sm font-light">{item.desc}</p>
                    </div>
                  </div>
                </FadeInWhenVisible>
              );
            })}
          </div>

          {/* Full Calendar View Section */}
          <FadeInWhenVisible delay={0.5}>
            <div className="mb-12 flex flex-col gap-8 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <h2 className="text-3xl md:text-5xl font-display font-bold text-brand-black mb-4 tracking-tighter">
                  Full <span className="blue-gradient-text">Class Schedule</span>
                </h2>
                <p className="text-gray-600 text-lg max-w-2xl">
                  Select a date and time that works for you. We offer flexible scheduling to fit your busy lifestyle.
                </p>
              </div>
              <div className="flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={handleDownloadScheduleImage}
                  disabled={isDownloading}
                  className="btn-premium btn-premium-outline-dark text-sm tracking-[0.2em] uppercase inline-flex items-center gap-2 disabled:opacity-60"
                >
                  <Download size={16} strokeWidth={2.5} />
                  {isDownloading ? 'Preparing…' : 'Download Schedule (Photo)'}
                </button>
                <TrackedBookingLink
                  href="/#book-trial"
                  className="btn-premium btn-premium-blue text-sm tracking-[0.2em] uppercase"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Book Your Free Trial Class
                </TrackedBookingLink>
              </div>
            </div>
            <div className="bg-white rounded-sm border border-gray-100 p-4 md:p-8">
              <ClassCalendar />
            </div>

            <div className="mt-12">
              <div ref={scheduleTableRef}>
                <WeeklyScheduleTable />
              </div>
            </div>

            {/* Schedule Description & CTA Under Calendar */}
            <div className="mt-12 md:mt-16 max-w-4xl mx-auto text-center">
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-6">
                Delta BJJ offers flexible{' '}
                <a
                  href="https://www.deltabjj.com/brazilian-jiu-jitsu-training-programs"
                  className="text-brand-blue underline decoration-2 underline-offset-2"
                >
                  Brazilian Jiu-Jitsu training
                </a>{' '}
                led by{' '}
                <a
                  href="https://www.deltabjj.com/instructors"
                  className="text-brand-blue underline decoration-2 underline-offset-2"
                >
                  industry-leading instructors
                </a>
                . For those with busy lifestyles, we offer classes in the morning, midday, and evening.
                Specializing in both Gi Jiu-Jitsu and No-Gi Jiu-Jitsu, our curriculum covers everything from
                fundamentals and skills development to advanced techniques and Judo for BJJ.
              </p>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8">
                Review our weekly classes below and select a day and time that works best with your schedule.{' '}
                <a
                  href="https://www.deltabjj.com/contact-bjj-culver-city"
                  className="text-brand-blue underline decoration-2 underline-offset-2"
                >
                  Contact us
                </a>{' '}
                today to book your free trial class! We look forward to seeing you on the mats!
              </p>
              <TrackedBookingLink
                href="/#book-trial"
                className="btn-premium btn-premium-blue"
                target="_blank"
                rel="noopener noreferrer"
              >
                BOOK YOUR FREE TRIAL CLASS
              </TrackedBookingLink>
            </div>
          </FadeInWhenVisible>
        </div>
      </section>

      {/* CTA section */}
      <section className="py-32 md:py-48 px-6 lg:px-12 text-center bg-brand-black text-white">
        <FadeInWhenVisible>
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-7xl font-display font-bold mb-12 leading-none tracking-tighter">
              READY TO <br />
              <span className="blue-gradient-text">ROLL?</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-8">
              <TrackedBookingLink href="/#book-trial" className="btn-premium-blue">Book Your Spot</TrackedBookingLink>
              <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="btn-premium-outline">
                Call: {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </FadeInWhenVisible>
      </section>
    </div>
  );
}
